import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrdersManagement = ({ orders, onRefresh }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendiente', class: 'bg-yellow-600' },
      approved: { label: 'Aprobado', class: 'bg-green-600' },
      processing: { label: 'Procesando', class: 'bg-blue-600' },
      shipped: { label: 'Enviado', class: 'bg-purple-600' },
      delivered: { label: 'Entregado', class: 'bg-emerald-600' },
      cancelled: { label: 'Cancelado', class: 'bg-red-600' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-slate-600' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('krolbolt_token');
      const response = await fetch(`${API}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error actualizando estado');
      }
      onRefresh();
      alert('Estado del pedido actualizado correctamente');
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar el estado del pedido: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Pedidos Activos ({orders.length})</h2>
      <div className="space-y-4">
        {orders.map((order) => (
          <Card key={order.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-white">{order.customer_name}</CardTitle>
                  <CardDescription className="text-slate-400">Pedido #{order.order_number}</CardDescription>
                </div>
                {getStatusBadge(order.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Email:</span>
                  <p className="text-white">{order.customer_email}</p>
                </div>
                <div>
                  <span className="text-slate-400">Teléfono:</span>
                  <p className="text-white">{order.customer_phone}</p>
                </div>
                <div>
                  <span className="text-slate-400">Entrega:</span>
                  <p className="text-white">{order.delivery_method === 'pickup' ? 'Retiro en local' : 'Envío a domicilio'}</p>
                </div>
                <div>
                  <span className="text-slate-400">Estado de Pago:</span>
                  <p className="text-white font-semibold">
                    {order.payment_status === 'pagado' && <span className="text-emerald-400">✓ Pagado</span>}
                    {order.payment_status === 'paga al retirar' && <span className="text-yellow-400">💵 Paga al retirar</span>}
                    {order.payment_status === 'pendiente' && <span className="text-orange-400">⏳ Pendiente</span>}
                    {!order.payment_status && <span className="text-slate-400">Sin info</span>}
                  </p>
                </div>
                {order.shipping_address && (
                  <div className="col-span-2">
                    <span className="text-slate-400">Dirección:</span>
                    <p className="text-white">{order.shipping_address}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-700 pt-4">
                <p className="text-slate-400 text-sm mb-2">Productos:</p>
                {order.items.map((item, idx) => (
                  <div key={`${order.id || order.order_id}-item-${idx}-${item.name}`} className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">{item.name} x{item.quantity}</span>
                    <span className="text-white">${(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center border-t border-slate-700 pt-4">
                <span className="text-slate-400 font-semibold">Total:</span>
                <span className="text-cyan-400 font-bold text-2xl">${order.total_amount.toLocaleString()}</span>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <Label className="text-slate-300 text-sm mb-2 block">Cambiar Estado:</Label>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => updateOrderStatus(order.id, 'processing')}
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={order.status === 'processing'}
                  >
                    Procesando
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateOrderStatus(order.id, 'shipped')}
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={order.status === 'shipped'}
                  >
                    Enviado
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateOrderStatus(order.id, 'delivered')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={order.status === 'delivered'}
                  >
                    Entregado
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateOrderStatus(order.id, 'cancelled')}
                    variant="destructive"
                    disabled={order.status === 'cancelled'}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {orders.length === 0 && (
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardContent className="p-8 text-center">
              <p className="text-slate-400">No hay pedidos activos</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OrdersManagement;
